import React from 'react';
import { motion } from 'framer-motion';
import { X, Info, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/use-profile';
import { useNavigate } from 'react-router-dom';

import BulletinLikeHandler from '../BulletinLikeHandler';
import BulletinImage from '../BulletinImage';

interface RelicStageTwoProps {
  bulletin: any;
  onClose: () => void;
  onInfoClick: () => void;
}

const RelicStageTwo: React.FC<RelicStageTwoProps> = ({
  bulletin,
  onClose,
  onInfoClick
}) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { profile: author } = useProfile(bulletin.user_id);
  const navigate = useNavigate();

  const handleAuthorClick = () => {
    if (author?.username) {
      navigate(`/profile/${author.username}`);
    }
  };

  return (
    <BulletinLikeHandler
      bulletinId={bulletin.id}
      initialLikeCount={bulletin.like_count || 0}
      userId={user?.id}
      currentImageUrl={bulletin.image_url}
      currentThumbnailUrl={bulletin.thumbnail_url}
    >
      {(hasLiked, currentLikeCount, handleDoubleClick) => (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="relative w-full h-full flex items-center justify-center"
        >
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white border border-white/20"
            style={{ backdropFilter: 'blur(8px)' }}
          >
            <X className="h-5 w-5" />
          </Button>

          {/* Image container - allow near full viewport; wrapper is not rounded */}
          <div className="relative w-full h-full flex items-center justify-center">
            <div
              className=""
              style={{
                width: '90vw',
                height: '90vh',
                maxWidth: '90vw',
                maxHeight: '90vh'
              }}
            >
              <BulletinImage
                src={bulletin.image_url}
                fileType={bulletin.file_type}
                isSelected={true}
                onClick={() => {}}
                onLoad={() => {}}
                onError={() => {}}
                size="large"
                isMature={bulletin.is_mature}
              />
            </div>
            
            {/* Author pill - positioned below image */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
              <div 
                onClick={handleAuthorClick}
                className={`backdrop-blur-md rounded-full px-3 py-2 border shadow-xl cursor-pointer hover:scale-105 transition-transform duration-200 ${
                  author?.is_admin
                    ? 'bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-red-500/20 border-amber-400/30'
                    : author?.is_premium
                    ? 'bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 border-purple-400/30'
                    : 'bg-white/10 border-white/20'
                }`}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8 ring-2 ring-white/30">
                    <AvatarImage src={author?.profile_image_url || undefined} />
                    <AvatarFallback className="bg-white/20 text-white text-sm font-semibold">
                      {author?.display_name?.[0] || author?.username?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-white text-sm font-medium">
                    @{author?.display_name || author?.username || 'Anonymous'}
                  </span>
                </div>
              </div>
            </div>

            {/* Like counter and Info icon button - positioned lower at bottom */}
            <div className="absolute bottom-2 right-4 flex items-center gap-2">
              {/* Like counter */}
              <div className="bg-white/10 backdrop-blur-md rounded-full px-2 py-1 border border-white/20 shadow-xl">
                <div className="flex items-center gap-1">
                  <Heart className={`h-3 w-3 ${hasLiked ? 'fill-current text-pink-400' : 'text-white/80'}`} />
                  <span className="text-white text-xs font-medium">{currentLikeCount}</span>
                </div>
              </div>
              
              {/* Info icon button */}
              <Button
                onClick={onInfoClick}
                variant="ghost"
                size="icon"
                className="h-8 w-8 bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-md rounded-full transition-all duration-200 shadow-lg"
              >
                <Info className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </BulletinLikeHandler>
  );
};

export default RelicStageTwo;